/**
 * BartenderConfig
 * ───────────────
 * BarTender 打印服务配置页面
 * - 服务连接（URL / 认证）
 * - BTW 模板参数
 * - 系统字段 → 模板变量映射（含新版收货人字段）
 * - 行为策略开关
 * - 快速测试
 * - 所有配置持久化到 localStorage
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// ─── Types ────────────────────────────────────────────────────
type AuthType = 'basic' | 'token' | 'none';

interface FieldMapping {
   sysField: string;
   sysLabel: string;
   btVar: string;
   editable: boolean;
}

interface BartenderCfg {
   apiUrl: string;
   authType: AuthType;
   username: string;
   password: string;
   token: string;
   templatePath: string;
   printer: string;
   copies: number;
   mappings: FieldMapping[];
   autoSave: boolean;
   retryOnFail: boolean;
   dupWarning: boolean;
}

// ─── Defaults ─────────────────────────────────────────────────
const DEFAULT_MAPPINGS: FieldMapping[] = [
   { sysField: 'tracking_no', sysLabel: '运单号', btVar: 'OrderNumber', editable: true },
   { sysField: 'weight', sysLabel: '重量', btVar: 'GrossWeight', editable: true },
   { sysField: 'receiver_name', sysLabel: '收货人', btVar: 'ReceiverName', editable: true },
   { sysField: 'receiver_phone', sysLabel: '收货电话', btVar: 'ReceiverPhone', editable: true },
   { sysField: 'receiver_address', sysLabel: '收货地址', btVar: 'ReceiverAddr', editable: true },
   { sysField: 'shipper_name', sysLabel: '发货人', btVar: 'SenderName', editable: true },
   { sysField: 'batch_no', sysLabel: '批次号', btVar: 'BatchNo', editable: true },
   { sysField: 'dimensions', sysLabel: '尺寸(LWH)', btVar: 'DimsString', editable: true },
];

const DEFAULT_CFG: BartenderCfg = {
   apiUrl: 'bt2022.hedui.com/integration',
   authType: 'basic',
   username: 'admin_print',
   password: '',
   token: '',
   templatePath: 'C:\\BarTender\\Templates\\Waybill_Standard_2024.btw',
   printer: 'Zebra ZT410 (Warehouse A)',
   copies: 1,
   mappings: DEFAULT_MAPPINGS,
   autoSave: true,
   retryOnFail: true,
   dupWarning: false,
};

const STORAGE_KEY = 'hedui-bartender-cfg';

function loadCfg(): BartenderCfg {
   try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
         const parsed = JSON.parse(raw) as Partial<BartenderCfg>;
         // Merge to ensure new fields (e.g. receiver_*) always present
         return {
            ...DEFAULT_CFG,
            ...parsed,
            mappings: parsed.mappings?.length ? parsed.mappings : DEFAULT_MAPPINGS,
         };
      }
   } catch { /* ignore */ }
   return DEFAULT_CFG;
}

// ─── Toggle Switch ─────────────────────────────────────────────
const Toggle: React.FC<{
   id: string;
   checked: boolean;
   onChange: (v: boolean) => void;
}> = ({ id, checked, onChange }) => (
   <div className="relative inline-flex items-center">
      <input
         id={id}
         type="checkbox"
         checked={checked}
         onChange={e => onChange(e.target.checked)}
         className="sr-only peer"
      />
      <label
         htmlFor={id}
         className="w-11 h-6 bg-white/10 rounded-full cursor-pointer peer-checked:bg-primary transition-colors border border-white/10 peer-checked:border-primary/50 relative
                after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:w-5 after:h-5 after:transition-all peer-checked:after:translate-x-5"
      />
   </div>
);

// ─── InputField ────────────────────────────────────────────────
const InputField: React.FC<{
   label: string;
   value: string;
   onChange: (v: string) => void;
   type?: string;
   placeholder?: string;
   prefix?: string;
   suffix?: React.ReactNode;
   hint?: string;
   mono?: boolean;
}> = ({ label, value, onChange, type = 'text', placeholder, prefix, suffix, hint, mono }) => (
   <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</label>
      <div className="relative flex items-center">
         {prefix && (
            <span className="absolute left-3 text-sm text-gray-500 pointer-events-none select-none">{prefix}</span>
         )}
         <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-white dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl py-3 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-sm shadow-sm transition-all
                    ${prefix ? 'pl-10' : 'pl-4'} ${suffix ? 'pr-12' : 'pr-4'}
                    ${mono ? 'font-mono' : ''}
                `}
         />
         {suffix && (
            <div className="absolute right-3">{suffix}</div>
         )}
      </div>
      {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
   </div>
);

// ─── Section ───────────────────────────────────────────────────
const Section: React.FC<{
   icon: string;
   iconColor?: string;
   title: string;
   badge?: React.ReactNode;
   children: React.ReactNode;
}> = ({ icon, iconColor = 'text-primary', title, badge, children }) => (
   <section className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-200 dark:border-white/5 overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
         <div className="flex items-center gap-2.5">
            <span className={`material-icons text-xl ${iconColor}`}>{icon}</span>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h2>
         </div>
         {badge}
      </div>
      <div className="p-5 space-y-4">{children}</div>
   </section>
);

// ─── Main Component ────────────────────────────────────────────
const BartenderConfig: React.FC = () => {
   const navigate = useNavigate();
   const [cfg, setCfg] = useState<BartenderCfg>(loadCfg);
   const [showPassword, setShowPassword] = useState(false);
   const [testNo, setTestNo] = useState('');
   const [testing, setTesting] = useState(false);
   const [testResult, setTestResult] = useState<'idle' | 'ok' | 'fail'>('idle');
   const [saving, setSaving] = useState(false);
   const [dirty, setDirty] = useState(false);

   // Track unsaved changes
   const update = useCallback(<K extends keyof BartenderCfg>(key: K, val: BartenderCfg[K]) => {
      setCfg(prev => ({ ...prev, [key]: val }));
      setDirty(true);
   }, []);

   const updateMapping = (idx: number, newVar: string) => {
      const next = cfg.mappings.map((m, i) => i === idx ? { ...m, btVar: newVar } : m);
      update('mappings', next);
   };

   const resetMappings = () => {
      update('mappings', DEFAULT_MAPPINGS);
      toast.success('字段映射已重置为默认值');
   };

   // ── Connection test ──────────────────────────────────────────
   const handleTest = async () => {
      setTesting(true);
      setTestResult('idle');
      await new Promise(r => setTimeout(r, 1500));
      // Simulate: if URL contains 'hedui' → success
      const ok = cfg.apiUrl.includes('hedui') || cfg.apiUrl.includes('local');
      setTestResult(ok ? 'ok' : 'fail');
      setTesting(false);
      toast[ok ? 'success' : 'error'](ok ? '连接成功！服务响应正常' : '连接失败，请检查服务地址');
   };

   // ── Test print ───────────────────────────────────────────────
   const handleTestPrint = async () => {
      if (!testNo.trim()) { toast.error('请输入测试运单号'); return; }
      toast.loading('正在发送测试打印任务...', { id: 'test-print' });
      await new Promise(r => setTimeout(r, 1200));
      toast.success(`测试任务已发送：${testNo}`, { id: 'test-print' });
   };

   // ── Save ─────────────────────────────────────────────────────
   const handleSave = async () => {
      setSaving(true);
      await new Promise(r => setTimeout(r, 600));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
      setSaving(false);
      setDirty(false);
      toast.success('配置已保存', { icon: '✓' });
   };

   // ── Prompt on back if dirty ───────────────────────────────────
   const handleBack = () => {
      if (dirty) {
         if (!window.confirm('有未保存的修改，确认离开？')) return;
      }
      navigate(-1);
   };

   const connStatusColor =
      testResult === 'ok' ? 'bg-green-500' :
         testResult === 'fail' ? 'bg-red-500' :
            'bg-yellow-400';
   const connStatusLabel =
      testResult === 'ok' ? '已验证' :
         testResult === 'fail' ? '连接失败' :
            '未测试';

   return (
      <div className="bg-background-light dark:bg-background-dark text-slate-800 dark:text-slate-100 min-h-screen flex flex-col font-display pb-28 relative">

         {/* ── Header ── */}
         <header className="bg-white/80 dark:bg-surface-dark/80 backdrop-blur-md sticky top-0 z-40 border-b border-gray-200 dark:border-white/5 shadow-sm">
            <div className="px-4 py-3 flex items-center justify-between max-w-2xl mx-auto w-full">
               <div className="flex items-center gap-3">
                  <button
                     onClick={handleBack}
                     className="p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 transition-colors active:scale-90"
                  >
                     <span className="material-icons">arrow_back</span>
                  </button>
                  <div>
                     <h1 className="text-base font-bold text-slate-900 dark:text-white leading-tight">BarTender 配置</h1>
                     <p className="text-[11px] text-slate-500 dark:text-slate-400">Windows 打印服务集成</p>
                  </div>
               </div>
               <div className="flex items-center gap-2">
                  {dirty && (
                     <span className="text-[10px] text-amber-400 font-bold bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/30 animate-pulse">
                        未保存
                     </span>
                  )}
                  <div className="w-9 h-9 rounded-xl bg-blue-600/15 flex items-center justify-center">
                     <span className="material-icons text-blue-400 text-lg">print</span>
                  </div>
               </div>
            </div>
         </header>

         <main className="max-w-2xl mx-auto w-full p-4 space-y-4 flex-1">

            {/* ── Status Banner ── */}
            <div className={`flex items-center gap-3 p-3.5 rounded-2xl border text-sm transition-all ${testResult === 'ok'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                  : testResult === 'fail'
                     ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                     : 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300'
               }`}>
               <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${connStatusColor} ${testResult === 'idle' ? 'animate-pulse' : ''}`} />
               <div className="flex-1 min-w-0">
                  <span className="font-semibold">{connStatusLabel}</span>
                  {testResult === 'idle' && <span className="ml-2 text-xs opacity-70">点击「测试连接」验证服务可达性</span>}
                  {testResult === 'ok' && <span className="ml-2 text-xs opacity-70">BarTender 服务响应正常，可以打印</span>}
                  {testResult === 'fail' && <span className="ml-2 text-xs opacity-70">无法连接，请检查服务地址和认证信息</span>}
               </div>
               <span className="material-icons text-lg">
                  {testResult === 'ok' ? 'check_circle' : testResult === 'fail' ? 'error' : 'info'}
               </span>
            </div>

            {/* ── Section 1: Service Connection ── */}
            <Section icon="dns" title="服务连接">
               <InputField
                  label="API 服务地址 URL"
                  value={cfg.apiUrl}
                  onChange={v => update('apiUrl', v)}
                  prefix="https://"
                  placeholder="bt2022.hedui.com/integration"
                  mono
                  hint="填写 BarTender Integration Service 的完整地址（不含协议前缀）"
               />

               {/* Auth Type */}
               <div className="space-y-2">
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">认证方式</label>
                  <div className="grid grid-cols-3 gap-2">
                     {(['basic', 'token', 'none'] as AuthType[]).map(a => (
                        <button
                           key={a}
                           onClick={() => update('authType', a)}
                           className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${cfg.authType === a
                                 ? 'bg-primary/15 text-primary border-primary/40 shadow-sm'
                                 : 'bg-white dark:bg-black/20 text-gray-500 border-gray-200 dark:border-white/10 hover:border-primary/30'
                              }`}
                        >
                           {a === 'basic' ? 'Basic Auth' : a === 'token' ? 'Token' : '无认证'}
                        </button>
                     ))}
                  </div>
               </div>

               {/* Credentials */}
               {cfg.authType === 'basic' && (
                  <div className="grid grid-cols-2 gap-3">
                     <InputField
                        label="用户名"
                        value={cfg.username}
                        onChange={v => update('username', v)}
                        placeholder="admin_print"
                     />
                     <InputField
                        label="密码"
                        value={cfg.password}
                        onChange={v => update('password', v)}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        suffix={
                           <button
                              onClick={() => setShowPassword(s => !s)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                           >
                              <span className="material-icons text-base">
                                 {showPassword ? 'visibility_off' : 'visibility'}
                              </span>
                           </button>
                        }
                     />
                  </div>
               )}
               {cfg.authType === 'token' && (
                  <InputField
                     label="Bearer Token"
                     value={cfg.token}
                     onChange={v => update('token', v)}
                     type={showPassword ? 'text' : 'password'}
                     placeholder="eyJhbGciOiJ..."
                     mono
                     suffix={
                        <button
                           onClick={() => setShowPassword(s => !s)}
                           className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                           <span className="material-icons text-base">
                              {showPassword ? 'visibility_off' : 'visibility'}
                           </span>
                        </button>
                     }
                  />
               )}

               {/* Test Connection */}
               <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/5">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                     <span className={`w-2 h-2 rounded-full ${connStatusColor} ${testResult === 'idle' ? 'animate-pulse' : ''}`} />
                     {connStatusLabel}
                  </div>
                  <button
                     onClick={handleTest}
                     disabled={testing}
                     className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 disabled:opacity-50 transition-all active:scale-95"
                  >
                     <span className={`material-icons text-base ${testing ? 'animate-spin' : ''}`}>
                        {testing ? 'refresh' : 'wifi_tethering'}
                     </span>
                     {testing ? '检测中...' : '测试连接'}
                  </button>
               </div>
            </Section>

            {/* ── Section 2: Template Config ── */}
            <Section icon="description" title="模板参数">
               <InputField
                  label="BTW 模板文件路径"
                  value={cfg.templatePath}
                  onChange={v => update('templatePath', v)}
                  placeholder="C:\BarTender\Templates\*.btw"
                  mono
                  hint="填写 BarTender 服务器上的绝对路径（Windows 路径格式）"
                  suffix={<span className="material-icons text-gray-400 text-base">folder_open</span>}
               />
               <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5">
                     <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">默认打印机</label>
                     <select
                        value={cfg.printer}
                        onChange={e => update('printer', e.target.value)}
                        className="w-full bg-white dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl py-3 px-4 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                     >
                        <option>Zebra ZT410 (Warehouse A)</option>
                        <option>Zebra ZD620 (Warehouse B)</option>
                        <option>HPRT N41 (Office)</option>
                        <option>Datamax O'Neil</option>
                     </select>
                  </div>
                  <div className="space-y-1.5">
                     <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">打印份数</label>
                     <input
                        type="number"
                        min={1}
                        max={10}
                        value={cfg.copies}
                        onChange={e => update('copies', Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full bg-white dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl py-3 px-4 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-center font-bold"
                     />
                  </div>
               </div>
            </Section>

            {/* ── Section 3: Field Mapping ── */}
            <Section
               icon="compare_arrows"
               title="字段映射"
               badge={
                  <button
                     onClick={resetMappings}
                     className="text-xs font-semibold text-primary hover:text-primary/70 transition-colors flex items-center gap-1"
                  >
                     <span className="material-icons text-sm">restart_alt</span>
                     重置默认
                  </button>
               }
            >
               <p className="text-xs text-gray-500 -mt-1">将系统字段映射到 BTW 模板中的变量名</p>
               <div className="rounded-xl border border-gray-200 dark:border-white/8 overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-2 bg-gray-50 dark:bg-white/4 px-4 py-2.5 border-b border-gray-200 dark:border-white/5">
                     <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">系统字段</span>
                     <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">BTW 变量名</span>
                  </div>
                  {/* Rows */}
                  {cfg.mappings.map((row, i) => (
                     <div
                        key={row.sysField}
                        className={`grid grid-cols-2 items-center px-4 py-2.5 border-b border-gray-100 dark:border-white/4 last:border-0 transition-colors group hover:bg-gray-50 dark:hover:bg-white/3`}
                     >
                        {/* System field (read-only) */}
                        <div className="flex items-center gap-2">
                           <span className="font-mono text-[11px] bg-gray-100 dark:bg-white/8 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-md border border-gray-200 dark:border-white/10">
                              {row.sysField}
                           </span>
                           <span className="text-[11px] text-gray-500 hidden sm:inline">{row.sysLabel}</span>
                        </div>
                        {/* BTW variable (editable) */}
                        <div className="flex items-center gap-2">
                           <span className="material-icons text-gray-300 dark:text-gray-600 text-sm flex-shrink-0">arrow_forward</span>
                           <input
                              type="text"
                              value={row.btVar}
                              onChange={e => updateMapping(i, e.target.value)}
                              className="flex-1 font-mono text-xs bg-transparent border-0 border-b border-transparent focus:border-primary focus:ring-0 py-1 text-slate-900 dark:text-white placeholder-gray-400 outline-none transition-colors"
                              placeholder="VariableName"
                           />
                        </div>
                     </div>
                  ))}
               </div>
            </Section>

            {/* ── Section 4: Behavior Policies ── */}
            <Section icon="tune" title="行为策略">
               <div className="space-y-1">
                  {([
                     {
                        key: 'autoSave' as const,
                        label: '打印前自动保存',
                        desc: '提交任务前将先保存当前订单状态到数据库',
                        icon: 'save',
                     },
                     {
                        key: 'retryOnFail' as const,
                        label: '失败自动重试',
                        desc: '如果 BarTender 服务未响应，自动重连最多 3 次',
                        icon: 'refresh',
                     },
                     {
                        key: 'dupWarning' as const,
                        label: '重复打印确认',
                        desc: '检测到已打印的运单号时弹出二次确认警告',
                        icon: 'warning',
                     },
                  ] satisfies { key: keyof BartenderCfg & keyof Pick<BartenderCfg, 'autoSave' | 'retryOnFail' | 'dupWarning'>; label: string; desc: string; icon: string }[]).map(item => (
                     <div key={item.key} className="flex items-center gap-4 py-3 border-b border-gray-100 dark:border-white/4 last:border-0">
                        <div className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0">
                           <span className="material-icons text-base">{item.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                           <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.label}</p>
                           <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                        </div>
                        <Toggle
                           id={`toggle-${item.key}`}
                           checked={cfg[item.key] as boolean}
                           onChange={v => update(item.key, v as any)}
                        />
                     </div>
                  ))}
               </div>
            </Section>

            {/* ── Section 5: Quick Test ── */}
            <Section icon="science" iconColor="text-violet-400" title="快速测试">
               <p className="text-xs text-gray-500 -mt-1">输入一个运单号，验证打印数据能否正确发送到 BarTender</p>
               <div className="flex gap-3 items-end">
                  <InputField
                     label="测试运单号"
                     value={testNo}
                     onChange={setTestNo}
                     placeholder="例如: WAY-2024-001"
                     mono
                  />
                  <button
                     onClick={handleTestPrint}
                     className="flex-shrink-0 flex items-center gap-2 bg-violet-500/15 hover:bg-violet-500/25 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold py-3 px-4 rounded-xl transition-all active:scale-95 text-sm whitespace-nowrap mb-0"
                     style={{ marginBottom: 'calc(1.5rem + 2px)' }}
                  >
                     <span className="material-icons text-sm">send</span>
                     测试打印
                  </button>
               </div>
            </Section>

            <div className="h-4" />
         </main>

         {/* ── Sticky Footer ── */}
         <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/80 dark:bg-surface-dark/80 backdrop-blur-xl border-t border-gray-200 dark:border-white/5 p-4">
            <div className="max-w-2xl mx-auto flex gap-3">
               <button
                  onClick={() => navigate(-1)}
                  className="px-5 py-3.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-all active:scale-95"
               >
                  取消
               </button>
               <button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-primary/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
               >
                  {saving
                     ? <><span className="material-icons text-sm animate-spin">refresh</span> 保存中...</>
                     : <><span className="material-icons text-sm">{dirty ? 'save' : 'check'}</span> {dirty ? '保存配置' : '已是最新'}</>
                  }
               </button>
            </div>
         </div>
      </div>
   );
};

export default BartenderConfig;